/**
 * Platform stock options, used ONLY while `GET /playbook/v1/library` is in
 * flight or failing. Mirrors the LIVE (default) half of the tenant seed
 * (`services/tenant/src/seeds/enum-seed.ts` + `@gridiron/shared`
 * STOCK_STAT_OPTIONS). Never the primary read path: the live library is what
 * the plays service validates against.
 */
import type { StatCategory, StatOption, StatSide } from '../../lib/api-types';

import { isDesignTypeCategory } from './design-type';
import { STOCK_CONCEPT_DESIGN_TYPES } from './stat-catalog';

type Row = [StatCategory, string, string];

const ROWS: readonly Row[] = [
  ['FORMATION', 'Spread', 'Spread'],
  ['FORMATION', 'Trips', 'Trips'],
  ['FORMATION', 'Empty', 'Empty'],
  ['FORMATION', 'Pro-Spread', 'Pro Spread'],
  ['FORMATION', 'Pro-Trips', 'Pro Trips'],
  ['FORMATION', 'Trey', 'Trey'],
  ['FORMATION', 'Quads', 'Quads (4x1)'],
  ['FORMATION', 'Bunch', 'Bunch'],
  ['FORMATION', 'Ace', 'Ace'],
  ['FORMATION', 'Heavy', 'Heavy'],
  ['FORMATION', 'Stack', 'Stack'],
  ['FORMATION', 'Twins', 'Twins'],
  ['FORMATION', 'Trips-Bunch', 'Trips Bunch'],
  ['FORMATION', 'Empty-Bunch', 'Empty Bunch'],
  ['FORMATION', 'Unbalanced', 'Unbalanced'],
  ['COVERAGE', 'Cover0', '0'],
  ['COVERAGE', 'Cover1', '1'],
  ['COVERAGE', 'Cover2', '2'],
  ['COVERAGE', 'Cover3', '3'],
  ['COVERAGE', 'Tampa2', 'Tampa 2'],
  ['COVERAGE', 'Cover4', '4'],
  ['COVERAGE', 'Cover6', '6'],
  ['COVERAGE', '2-Man', '2-Man'],
  ['FRONT', '3-3', '3-3'],
  ['FRONT', '3-4', '3-4'],
  ['FRONT', '4-4', '4-4'],
  ['FRONT', '4-3', '4-3'],
  ['FRONT', '4-2', '4-2'],
  ['FRONT', '5-2', '5-2'],
  ['FRONT', 'Bear', 'Bear'],
  ['PERSONNEL', '10', '1 RB, 0 TE, 4 WR'],
  ['PERSONNEL', '11', '1 RB, 1 TE, 3 WR'],
  ['PERSONNEL', '12', '1 RB, 2 TE, 2 WR'],
  ['PERSONNEL', '21', '2 RB, 1 TE, 2 WR'],
  ['PERSONNEL', '22', '2 RB, 2 TE, 1 WR'],
  ['PERSONNEL', '13', '1 RB, 3 TE, 1 WR'],
  ['PERSONNEL', '20', '2 RB, 0 TE, 3 WR'],
  ['PERSONNEL', '23', '2 RB, 3 TE, 0 WR'],
  ['PERSONNEL', '32', '3 RB, 2 TE, 0 WR'],
  ['PERSONNEL', 'Heavy', 'Heavy'],
  ['MOTION', 'Jet', 'Jet Motion'],
  ['MOTION', 'Orbit', 'Orbit Motion'],
  ['PROTECTION', 'Man', 'Man Protection'],
  ['PROTECTION', 'Zone', 'Zone Protection'],
  ['PROTECTION', 'Slide', 'Slide Protection'],
  ['OFF_CONCEPT', 'Inside-Zone', 'Inside Zone'],
  ['OFF_CONCEPT', 'Outside-Zone', 'Outside Zone'],
  ['OFF_CONCEPT', 'Power', 'Power'],
  ['OFF_CONCEPT', 'Counter', 'Counter'],
  ['OFF_CONCEPT', 'Dive', 'Dive'],
  ['OFF_CONCEPT', 'Jet', 'Jet'],
  ['OFF_CONCEPT', 'Draw', 'Draw'],
  ['OFF_CONCEPT', 'QB-Sneak', 'QB Sneak'],
  ['OFF_CONCEPT', 'Screen', 'Screen'],
  ['OFF_CONCEPT', 'Curl', 'Curl'],
  ['OFF_CONCEPT', 'Slant', 'Slant'],
  ['OFF_CONCEPT', 'Smash', 'Smash'],
  ['OFF_CONCEPT', 'Verticals', 'Verticals'],
  ['OFF_CONCEPT', 'Bubble', 'Bubble'],
  ['OFF_CONCEPT', 'Hitch', 'Hitch'],
  ['OFF_CONCEPT', 'Trap', 'Trap'],
  ['DEF_CONCEPT', 'Base', 'Base'],
  ['DEF_CONCEPT', 'Nickel', 'Nickel'],
  ['DEF_CONCEPT', 'Dime', 'Dime'],
  ['DEF_CONCEPT', 'Goal-Line', 'Goal Line'],
  ['DEF_CONCEPT', 'Prevent', 'Prevent'],
  ['BLITZ', 'Mike', 'Mike'],
  ['BLITZ', 'Will', 'Will'],
  ['BLITZ', 'Sam', 'Sam'],
  ['BLITZ', 'Corner', 'Corner'],
  ['BLITZ', 'Safety', 'Safety'],
  ['BLITZ', 'Zero', 'Zero (all-out)'],
  ['ST_CONCEPT', 'Kickoff', 'Kickoff'],
  ['ST_CONCEPT', 'Onside', 'Onside Kick'],
  ['ST_CONCEPT', 'Squib', 'Squib Kick'],
  ['ST_CONCEPT', 'Punt', 'Punt'],
  ['ST_CONCEPT', 'Field-Goal', 'Field Goal'],
  ['ST_CONCEPT', 'PAT', 'PAT'],
  ['ST_CONCEPT', 'Fake', 'Fake'],
  ['ST_CONCEPT', 'Return', 'Return'],
  ['ST_FORMATION', 'Spread-Punt', 'Spread Punt'],
  ['ST_FORMATION', 'Shield-Punt', 'Shield Punt'],
  ['ST_FORMATION', 'FG-Standard', 'FG Standard'],
  ['ST_FORMATION', 'KO-Standard', 'Kickoff 5-5'],
  ['ST_FORMATION', 'KO-Onside', 'Onside Alignment'],
  ['ST_FORMATION', 'Return-Standard', 'Return Standard'],
  ['ST_FORMATION', 'Hands-Team', 'Hands Team'],
  ['ST_PERSONNEL', 'Kickoff', 'Kickoff Unit'],
  ['ST_PERSONNEL', 'KO-Return', 'Kick Return Unit'],
  ['ST_PERSONNEL', 'Punt', 'Punt Unit'],
  ['ST_PERSONNEL', 'Punt-Return', 'Punt Return Unit'],
  ['ST_PERSONNEL', 'FG-PAT', 'FG / PAT Unit'],
  ['ST_PERSONNEL', 'FG-Block', 'FG Block Unit'],
  ['ST_PERSONNEL', 'Hands', 'Hands Team'],
  ['ST_PROTECTION', 'Man', 'Man'],
  ['ST_PROTECTION', 'Zone', 'Zone'],
  ['ST_PROTECTION', 'Shield', 'Shield'],
  ['ST_COVERAGE_SCHEME', 'Lane', 'Lane Coverage'],
  ['ST_COVERAGE_SCHEME', 'Directional', 'Directional'],
  ['ST_COVERAGE_SCHEME', 'Contain', 'Contain'],
  ['ST_RETURN_CONCEPT', 'Middle', 'Middle Return'],
  ['ST_RETURN_CONCEPT', 'Right', 'Right Return'],
  ['ST_RETURN_CONCEPT', 'Left', 'Left Return'],
  ['ST_RETURN_CONCEPT', 'Wall', 'Wall'],
  ['ST_RETURN_CONCEPT', 'Wedge', 'Wedge'],
  ['ST_RETURN_CONCEPT', 'Fair-Catch', 'Fair Catch'],
  ['ST_RUSH_BLOCK_SCHEME', 'Safe', 'Safe (no rush)'],
  ['ST_RUSH_BLOCK_SCHEME', 'Edge-Rush', 'Edge Rush'],
  ['ST_RUSH_BLOCK_SCHEME', 'Middle-Rush', 'Middle Rush'],
  ['ST_RUSH_BLOCK_SCHEME', 'Overload', 'Overload'],
  ['ST_RUSH_BLOCK_SCHEME', 'Block', 'All-out Block'],
  ['ST_OPPONENT_ALIGNMENT', 'Standard', 'Standard'],
  ['ST_OPPONENT_ALIGNMENT', 'Overload-Left', 'Overload Left'],
  ['ST_OPPONENT_ALIGNMENT', 'Overload-Right', 'Overload Right'],
  ['ST_OPPONENT_ALIGNMENT', 'Onside-Look', 'Onside Look'],
  ['ST_OPPONENT_ALIGNMENT', 'Block-Look', 'Block Look'],
];

function toOption(row: Row, side: StatSide, index: number): StatOption {
  return {
    option_id: `stock:${row[0]}:${side}:${row[1]}`,
    category: row[0],
    side,
    value: row[1],
    label: row[2],
    sort_order: (index + 1) * 10,
    is_stock: true,
    is_enabled: true,
    notes: null,
    glyph_svg: null,
    // Stock offensive concepts ship tagged (both sides), like the tenant seed.
    ...(isDesignTypeCategory(row[0])
      ? { design_type: STOCK_CONCEPT_DESIGN_TYPES[row[1]] ?? null }
      : {}),
    usage_count: 0,
    created_at: '',
    updated_at: '',
  };
}

export const STOCK_LIBRARY_FALLBACK: readonly StatOption[] = [
  ...ROWS.filter((row) => row[0] !== 'ST_OPPONENT_ALIGNMENT').map((row, i) =>
    toOption(row, 'own', i)
  ),
  ...ROWS.map((row, i) => toOption(row, 'opponent', i)),
];
